import GTMService from "@/services/utils/GTMService";
import AnalyticsService from "@/services/utils/AnalyticsService";
import React, { useEffect, useState } from 'react';
import { ToastProvider, useToasts } from 'react-toast-notifications';
import CartProvider from "../context/cart/CartProvider";
import ProductProvider from "../context/product/ProductProvider";
import UserProvider from "../context/user/UserProvider";
import { appWithTranslation } from '../i18n';
import Toast from "../shared/toast/Toast";
import ToastContainer from "../shared/toast/ToastContainer";
import '../static/styles/App.scss';
import Head from "next/head";
import LinksProvider from "@/services/http/LinksProvider";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useRouter } from "next/router";
import Script from "next/script";
import { useTranslation } from "../i18n";
import AuthService from "@/services/domain/AuthService";
import ErrorHandler from "@/services/error/ErrorHandler";
import NewsletterPopup from "@/shared/newsletter-popup/NewsletterPopup";
import PeriodicReviewModal from "@/components/dashboard/periodic-review/PeriodicReviewModal";
const packageJson = require('../../package.json');

const HOTJAR_ID = Number(process.env.REACT_HOTJAR_ID);
let hotjarLoaded = false;

// Sentry.init({
//   dsn: process.env.REACT_SENTRY_DSN,
//   environment: process.env.NODE_ENV,
//   release: packageJson.version,
//   debug: true,
//   attachStacktrace: true
// });

function getCanonicalUrl(path) {
  const baseUrl = process.env.REACT_FRONTEND_BASE_URL
  return `${baseUrl}${path}`;
}


const MyApp = ({Component, pageProps}) => {
  const router = useRouter();
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  const shouldLoadHotjar = () => {
    return AnalyticsService.isAnalyticsActive();
  };

  const injectHotjar = () => {
    if (hotjarLoaded || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    if (!Number.isFinite(HOTJAR_ID)) {
      return;
    }

    // Inline snippet from Hotjar
    // eslint-disable-next-line func-names
    (function (c, s, q, u, a, r, e) {
      c.hj = c.hj || function () {
        (c.hj.q = c.hj.q || []).push(arguments);
      };
      c._hjSettings = { hjid: a };
      r = s.getElementsByTagName('head')[0];
      e = s.createElement('script');
      e.id = 'hotjar-snippet';
      e.async = true;
      e.src = q + c._hjSettings.hjid + u;
      if (s.getElementById(e.id)) {
        hotjarLoaded = true;
        return;
      }
      r.appendChild(e);
    })(
      window,
      document,
      'https://static.hj.contentsquare.net/c/csq-',
      '.js',
      HOTJAR_ID
    );
    hotjarLoaded = true;
  };

  const tryLoadHotjar = () => {
    if (shouldLoadHotjar()) {
      injectHotjar();
    }
  };

  const { t } = useTranslation();
  const SessionExpiryListener = () => {
    const { addToast } = useToasts();

    useEffect(() => {
      const handler = () => addToast(t('common:errors.SESSION_EXPIRED'), {
        appearance: 'error',
        autoDismiss: true,
      });
      const unsubscribe = AuthService.onSessionExpired(handler);
      return () => unsubscribe();
    }, [addToast, t]);

    useEffect(() => {
      const pendingMessages = ErrorHandler.getCookieErrorMessages();
      if (pendingMessages && pendingMessages.length) {
        ErrorHandler.displayCookieErrorMessages(pendingMessages, addToast, t);
      }
    }, [addToast, t]);

    return null;
  };

  useEffect(() => {
    GTMService.initialize();

    // Restore consent state from localStorage on page load (GL-5009)
    try {
      const marketing = localStorage.getItem('marketing_active');
      const analytics = localStorage.getItem('analytics_active');
      if (marketing === 'enable' || analytics === 'enable') {
        GTMService.sendEvent('consent_update');
      }
    } catch (e) { /* localStorage unavailable */ }
  }, []);

  useEffect(() => {
    const handler = () => setNewsletterOpen(true);
    if (typeof window !== 'undefined') {
      window.addEventListener('showNewsletterPopup', handler);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('showNewsletterPopup', handler);
      }
    };
  }, []);

  useEffect(() => {
    const onRouteChangeComplete = () => {
      tryLoadHotjar();
    };

    const onAnalyticsConsentChanged = () => {
      tryLoadHotjar();
    };

    tryLoadHotjar();
    router.events.on('routeChangeComplete', onRouteChangeComplete);
    if (typeof window !== 'undefined') {
      window.addEventListener(AnalyticsService.ANALYTICS_CONSENT_EVENT_NAME, onAnalyticsConsentChanged);
    }

    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
      if (typeof window !== 'undefined') {
        window.removeEventListener(AnalyticsService.ANALYTICS_CONSENT_EVENT_NAME, onAnalyticsConsentChanged);
      }
    };
  }, [router.events, router.pathname]);

  return (
    <React.Fragment>
      <Head>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
          name="viewport"
        />
        <link rel="canonical" href={getCanonicalUrl(router.asPath)} />
        <title>Liste de mariage unique et personnalisée | Mille et une listes</title>
        {/* Critical CSS */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .header { opacity: 0; }
            .header.visible { opacity: 1; transition: opacity 0.3s ease-in; }
            .btn__container { opacity: 0; transform: translateY(20px); }
            .btn__container.visible { opacity: 1; transform: translateY(0); transition: all 0.3s ease-out; }
          `
        }} />

        {/* Meta tags */}
        <meta name="description"
              content="La liste de mariage gratuite et personnalisée pour se faire plaisir aux Galeries Lafayette, chez La Redoute, au BHV Marais et en récupérant l'argent sur son compte bancaire" key={"description"}/>
        <meta property="og:type" content="website" key={"og_type"}/>
        <meta property="og:title" content="Liste de mariage unique et personnalisée | Mille et une listes" key={"og_title"}/>
        <meta property="og:url" content={LinksProvider.getFullRoute(LinksProvider.ROUTES.HOME)} key={"og_url"}/>
        <meta property="og:description"
              content="Le luxe du choix avec la liste de mariage des Galeries Lafayette et du BHV MARAIS" key={"og_description"}/>
        <meta property="fb:app_id" content={process.env.REACT_FACEBOOK_APP_ID} key={"fb_app_id"}/>
        <meta property="og:image" content={LinksProvider.getFullRoute('/static/images/logos/og-image.webp')} key={"og_image"}/>
        <meta property="og:image:width" content="1200" key={"og_image_width"}/>
        <meta property="og:image:height" content="630" key={"og_image_height"}/>
        <meta name="google-site-verification" content={process.env.REACT_APP_GOOGLE_SITE_VERIFICATION} />
      </Head>
      <GoogleOAuthProvider clientId={process.env.REACT_GOOGLE_APP_ID}>
        <ToastProvider components={{Toast: Toast, ToastContainer: ToastContainer}}>
          <SessionExpiryListener />
          <UserProvider>
            <ProductProvider>
              <CartProvider>
                <Component {...pageProps} />
                <NewsletterPopup
                  open={newsletterOpen}
                  onClose={() => setNewsletterOpen(false)}
                />
                <PeriodicReviewModal />
              </CartProvider>
            </ProductProvider>
          </UserProvider>
        </ToastProvider>
      </GoogleOAuthProvider>
      {/* Zendesk (load after app is interactive) */}
    <Script
      id="ze-snippet"
      src="https://static.zdassets.com/ekr/snippet.js?key=1d63434a-08ba-4cdf-a2d5-8beb52ab28b1"
      strategy="afterInteractive"
    />
    </React.Fragment>
  );
};


// getInitialProps removed - pages now use getServerSideProps directly

export default appWithTranslation(MyApp);
